import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { HoldRestrictionType, HoldTypeRestrictionDto } from '@planning/app/api'
import { useTranslate } from '@tolgee/react'
import { ColoredLabel, ConfirmationDialog, useMinimalsTheme } from '@tom-ui/ui'
import { appStore } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'

interface Props {
  open: boolean
  onClose: () => void
  onConfirm: (operationTypes: HoldRestrictionType[], reason: string) => Promise<void>
  holdTypeName?: string
  containerNumber?: string
  restrictions?: Array<HoldTypeRestrictionDto> | null
}

const ALL_OPERATION_TYPES = [
  HoldRestrictionType.Entry,
  HoldRestrictionType.Exit,
  HoldRestrictionType.Yard,
] as const

export const HoldOverrideDialog = observer(
  ({ open, onClose, onConfirm, holdTypeName, containerNumber, restrictions }: Props) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()
  const [isLoading, setIsLoading] = useState(false)
  const [reason, setReason] = useState('')
  const [selectedTypes, setSelectedTypes] = useState<HoldRestrictionType[]>([])
  const [touched, setTouched] = useState(false)

  const availableTypes = restrictions?.length
    ? ALL_OPERATION_TYPES.filter(type => restrictions.some(r => r.restrictionType === type))
    : ALL_OPERATION_TYPES

  useEffect(() => {
    if (open) {
      setReason('')
      setSelectedTypes([])
      setTouched(false)
      setIsLoading(false)
    }
  }, [open])

  const toggleType = (type: HoldRestrictionType) => {
    setSelectedTypes(prev => (prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]))
  }

  const handleConfirm = async () => {
    setTouched(true)
    if (selectedTypes.length === 0 || !reason.trim()) return

    setIsLoading(true)
    try {
      await onConfirm(selectedTypes, reason.trim())
      appStore.setShowAlert('success', t('holds.override.success', 'Hold overridden successfully'))
    } catch {
      appStore.setShowAlert('error', t('holds.override.error', 'Failed to override hold'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <ConfirmationDialog
      open={open}
      isLoading={isLoading}
      title={t('overrideHold', 'Override hold')}
      primaryActionText={t('override', 'Override')}
      closeLabel={t('cancel', 'Cancel')}
      onClose={onClose}
      onConfirm={handleConfirm}
      maxWidth='xs'
    >
      <Stack gap={theme.customSpacing.m} sx={{ pt: theme.customSpacing.xs }}>
        {(holdTypeName || containerNumber) && (
          <Stack gap={theme.customSpacing.xxs}>
            {holdTypeName && (
              <Typography variant='body2'>
                <strong>{t('type', 'Type')}:</strong> {holdTypeName}
              </Typography>
            )}
            {containerNumber && (
              <Typography variant='body2'>
                <strong>{t('container', 'Container')}:</strong> {containerNumber}
              </Typography>
            )}
          </Stack>
        )}
        <Stack gap={theme.customSpacing.xxs}>
          <Typography variant='body2'>{t('operationTypes', 'Operation types')}</Typography>
          <FormGroup>
            {availableTypes.map(type => {
              const reaction = restrictions?.find(r => r.restrictionType === type)?.reaction
              return (
                <FormControlLabel
                  key={type}
                  control={
                    <Checkbox
                      checked={selectedTypes.includes(type)}
                      onChange={() => toggleType(type)}
                      size='small'
                    />
                  }
                  label={
                    <Stack direction='row' alignItems='center' gap={theme.customSpacing.xs}>
                      <Typography variant='body2'>
                        {t(`holdRestrictionType.${type.toLowerCase()}`, type)}
                      </Typography>
                      {reaction && (
                        <ColoredLabel
                          color='secondary'
                          label={t(`holdReaction.${reaction.toLowerCase()}`, reaction)}
                          sx={{ height: theme.customSpacing.l }}
                        />
                      )}
                    </Stack>
                  }
                />
              )
            })}
          </FormGroup>
          {touched && selectedTypes.length === 0 && (
            <FormHelperText error>
              {t('holds.override.operationTypeRequired', 'Select at least one operation type')}
            </FormHelperText>
          )}
        </Stack>
        <TextField
          label={t('reason', 'Reason')}
          multiline
          rows={2}
          value={reason}
          fullWidth
          onChange={e => setReason(e.target.value)}
          required
          error={touched && !reason.trim()}
          helperText={
            touched && !reason.trim()
              ? t('holds.override.reasonRequired', 'Reason is required')
              : undefined
          }
        />
      </Stack>
    </ConfirmationDialog>
  )
},
)
